import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

export const AutomationDialog = ({
  open,
  onClose,
  onSave,
  initialData = { videoLoopName: "", selectedVideos: [] },
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { token, user } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);

  const [selectedVideos, setSelectedVideos] = useState([]);
  const [availableVideos, setAvailableVideos] = useState([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false); // State for preview modal
  const [previewVideo, setPreviewVideo] = useState(null); // State for video details

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      videoLoopName: initialData?.videoLoopName || "",
    },
  });

  // **Fetch video details for selectedVideos**
  useEffect(() => {
    const fetchSelectedVideoDetails = async () => {
      const updatedSelectedVideos = [];
      for (const video of initialData.selectedVideos) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/video/${video.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          updatedSelectedVideos.push(response.data); // Push the fetched video details
        } catch (error) {
          console.error(`Error fetching video with ID ${video.id}:`, error);
        }
      }

      setSelectedVideos(updatedSelectedVideos); // Update selectedVideos with full details
    };

    if (open) fetchSelectedVideoDetails();
  }, [initialData?.selectedVideos, open, token]);

  // Fetch all available videos
  useEffect(() => {
    if (open && token) {
      const fetchVideos = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/video/withcustomvideos/${user?.practiceId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const allVideos = response.data || [];
          const selectedIds = selectedVideos.map((video) => video.id);
          setAvailableVideos(
            allVideos.filter((video) => !selectedIds.includes(video.id))
          );
        } catch (err) {
          console.error("Error fetching videos:", err);
        }
      };
      fetchVideos();
    }
  }, [open, token, selectedVideos, user]);

  // Handle moving videos between selected and available lists
  const handleSelectVideo = (video) => {
    setSelectedVideos((prev) => [...prev, video]);
    setAvailableVideos((prev) => prev.filter((v) => v.id !== video.id));
  };

  const handleDeselectVideo = (video) => {
    setAvailableVideos((prev) => [...prev, video]);
    setSelectedVideos((prev) => prev.filter((v) => v.id !== video.id));
  };

  const onSubmit = (data) => {
    if (!Array.isArray(selectedVideos) || selectedVideos.length === 0) {
      enqueueSnackbar("Select at least one video!", { variant: "error" });
      return;
    }

    const selectedVideoIds = selectedVideos.map((video) => video.id).join(",");

    onSave({
      id: initialData?.id,
      loopname: data.videoLoopName,
      practiceid: user?.practiceId,
      videocollection: `,${selectedVideoIds}`,
    });
    onClose();
  };

  const handlePreviewVideo = (videoLink, vimeoLink) => {
    setPreviewVideo({ videoLink, vimeoLink }); // Set video details
    setPreviewModalOpen(true); // Open the modal
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false); // Close the modal
    setPreviewVideo(null); // Clear video details
  };

  // Determine dialog title and submit button text
  const isEditMode = !!initialData?.id;
  const isCopyMode = initialData?.videoLoopName?.startsWith("Copy of");
  const dialogTitle = isEditMode
    ? "Edit Video Loop"
    : isCopyMode
    ? "Copy Video Loop"
    : "Add Video Loop";
  const submitButtonText = isEditMode ? "Save" : "Add";

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <TextField
              label="Video Loop Name"
              placeholder="Enter Video Loop name"
              {...register("videoLoopName", {
                required: "Video Loop Name is required",
                minLength: {
                  value: 3,
                  message: "Video Loop Name must be at least 3 characters long",
                },
                maxLength: {
                  value: 50,
                  message: "Video Loop Name cannot exceed 50 characters",
                },
              })}
              error={!!errors.videoLoopName}
              helperText={errors.videoLoopName?.message}
              fullWidth
              margin="normal"
            />
            <Box gap={2} sx={{ mt: 2 }}>
              {/* Selected Videos */}
              <Box flex={1}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "20px", sm: "20px" },
                    fontWeight: 500,
                    color: darkMode
                      ? getColors.thumbnailTextDark
                      : getColors.secondaryTextLight,
                    mb: 2,
                  }}
                >
                  Selected Videos
                </Typography>
                <Box
                  display="grid"
                  sx={{
                    gridTemplateColumns: {
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                    },
                  }}
                  gap={2}
                >
                  {Array.isArray(selectedVideos) &&
                    selectedVideos.map((video) => (
                      <Box
                        key={video.id}
                        onClick={() => handleDeselectVideo(video)}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                          cursor: "pointer",
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "14px",
                          fontFamily: "Poppins",
                          padding: "5px",
                          color: darkMode
                            ? getColors.thumbnailTextDark
                            : getColors.outlinedButtonText,
                          border: darkMode
                            ? `2px solid ${getColors.thumbnailTextDark}`
                            : `2px solid ${getColors.outlinedButtonText}`,
                          "&:hover": {
                            color: getColors.buttonText,
                            border: "2px solid transparent",
                            background: getColors.buttonBg,
                          },
                          borderRadius: "4px",
                        }}
                      >
                        {video.displayname} {/* Use the fetched displayname */}
                      </Box>
                    ))}
                </Box>
              </Box>
              {/* Available Videos */}
              <Box flex={1}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "20px", sm: "20px" },
                    fontWeight: 500,
                    color: darkMode
                      ? getColors.thumbnailTextDark
                      : getColors.secondaryTextLight,
                    mt: 3,
                    mb: 2,
                  }}
                >
                  Available Videos
                </Typography>
                <Box
                  display="grid"
                  sx={{
                    gridTemplateColumns: {
                      xs: "repeat(1, 1fr)",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                    },
                  }}
                  gap={2}
                >
                  {availableVideos.map((video) => (
                    <Box
                      key={video.id}
                      onClick={() => handleSelectVideo(video)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        textAlign: "center",
                        cursor: "pointer",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "14px",
                        fontFamily: "Poppins",
                        padding: "5px",
                        color: darkMode
                          ? getColors.thumbnailTextDark
                          : getColors.outlinedButtonText,
                        border: darkMode
                          ? `2px solid ${getColors.thumbnailTextDark}`
                          : `2px solid ${getColors.outlinedButtonText}`,
                        "&:hover": {
                          color: getColors.buttonText,
                          border: "2px solid transparent",
                          background: getColors.buttonBg,
                        },
                        borderRadius: "4px",
                      }}
                    >
                      <IconButton size="small" sx={{ visibility: "hidden" }}>
                        <PlayCircleIcon fontSize="small" />
                      </IconButton>
                      {video.displayname}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreviewVideo(video.videolink, video.vimeolink);
                        }}
                      >
                        <PlayCircleIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={onClose}
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: 500,
                color: darkMode
                  ? getColors.appBarBgLight
                  : getColors.appBarBgDark,
                textAlign: "center",
                "&: hover": {
                  background: "none",
                  color: getColors.primaryMain,
                },
                mr: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: 500,
                textAlign: "center",
                px: 4,
              }}
            >
              {submitButtonText}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        open={previewModalOpen}
        onClose={closePreviewModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Video Preview</DialogTitle>
        <DialogContent>
          {previewVideo?.vimeoLink === "YouTube" ? (
            <iframe
              width="100%"
              height="315"
              src={`https://www.youtube.com/embed/${previewVideo?.videoLink}?autoplay=1&enablejsapi=1&origin=${window.location.origin}`}
              title="YouTube video player"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            ></iframe>
          ) : (
            <video
              onContextMenu={(e) => e.preventDefault()}
              width="100%"
              height="315"
              controls
              controlsList="nodownload"
              oncontextmenu="return false;"
              src={`https://d2nut6gc5qzbiv.cloudfront.net/34481519/${previewVideo?.videoLink}`}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closePreviewModal}
            variant="contained"
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: 500,
              textAlign: "center",
              px: 4,
              mr: 2,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
